// src/components/math/MathRenderer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { MathUtils } from './MathUtils';

export const MathRenderer = ({ 
  content, 
  className = '',
  enableMarkdown = true,
  enableMath = true
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderContent = async () => {
      if (!containerRef.current || !content) {
        setIsReady(true);
        return;
      }

      try {
        setError(null);
        setIsReady(false);

        let finalContent = content;
        const hasMath = MathUtils.containsMathExpressions(content);

        if (enableMarkdown) {
          // Process with markdown (includes math normalization)
          finalContent = await MathUtils.processContentWithMarkdown(content);
        } else {
          // Normalize math delimiters even without markdown
          if (hasMath) {
            finalContent = MathUtils.processMathExpressions(content);
          }
          // Add line breaks
          finalContent = finalContent.replace(/\n/g, '<br>');
        }

        // Set the content in the container
        if (containerRef.current) {
          containerRef.current.innerHTML = finalContent;

          // Render math if enabled and math is present
          if (enableMath && hasMath) {
            await MathUtils.renderMath(containerRef.current);
          }
        }
        
        setIsReady(true);
      } catch (err) {
        console.error('Error rendering content:', err);
        setError(err.message);
        
        // Fallback: show content without processing
        const fallbackContent = content.replace(/\n/g, '<br>');
        if (containerRef.current) {
          containerRef.current.innerHTML = fallbackContent;
        }
        setIsReady(true);
      }
    };

    renderContent();
  }, [content, enableMarkdown, enableMath]);

  if (error) {
    return (
      <div className={`content-error ${className}`}>
        <div className="text-red-600 text-sm mb-2">
          Content rendering error: {error}
        </div>
        <div dangerouslySetInnerHTML={{ __html: content?.replace(/\n/g, '<br>') || '' }} />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`markdown-content ${className} ${!isReady ? 'content-loading' : ''}`}
      style={{
        lineHeight: '1.6',
        fontSize: '1rem',
        opacity: isReady ? 1 : 0.7
      }}
    />
  );
};